import { DEFAULT_CONFIRM_LIMITS, isWideConfirm } from './paneHost.js';
import { toolConnectorLabel } from './toolActivityLabels.js';
/**
 * What a confirm card shows of the exact input an action runs with (moved from GyroAssistant.tsx, owner 2026-10-12: "can
 * this parameter data shown in chat be normalized so it doesnt look like code?").
 *  - The exact view: a bounded, deterministic, credential-redacted JSON projection of the input (formatActionParameters).
 *    It is unchanged byte for byte, and whether it is complete still decides whether the card can be confirmed.
 *  - The readable view (readableParameters), the card's default: the same projection as one labelled row per field, dates
 *    written out, Yes/No, lists and indented sub-rows. It is built from the projection itself, so it shows exactly the
 *    fields, masks and "omitted" notes the exact view shows, and nothing the exact view cut. A value it cannot write out
 *    plainly is shown as its exact JSON, and an opaque id nobody can check by eye (a Teams chat id) is named with a pointer
 *    to the exact view, which shows it whole.
 */
const PARAMETER_MAX_DEPTH = 5;
const PARAMETER_MAX_ENTRIES = 40;
const PARAMETER_MAX_ARRAY_ITEMS = 25;
/** A wide (Office) card: only its character limits block Confirm; this depth only stops runaway nesting. */
const WIDE_PARAMETER_MAX_DEPTH = 32;
/** A complete wide card shows its parameters indented up to this many times its total limit, compact beyond that. */
const WIDE_INDENTED_DISPLAY_FACTOR = 4;
const SENSITIVE_PARAMETER_KEY = /(?:password|passwd|secret|token|authorization|api[_-]?key|apikey|private[_-]?key|credential|cookie|session[_-]?id|access[_-]?key|bearer)/i;
/**
 * A placeholder the projection puts where it cannot show a value whole. It serializes to exactly the text the exact view
 * has always shown ('[redacted]', '...[12 chars omitted]', '[3 items omitted]'), so that view is unchanged, while the
 * readable view can tell a placeholder from a value that only looks like one.
 */
export class ParameterNote {
    kind;
    text;
    shown;
    constructor(kind, text, shown = '') {
        this.kind = kind;
        this.text = text;
        this.shown = shown;
    }
    toJSON() { return this.shown + this.text; }
}
function safeParameterValue(value, key, depth, seen, state) {
    if (SENSITIVE_PARAMETER_KEY.test(key))
        return new ParameterNote('redacted', '[redacted]');
    if (value === null || typeof value === 'boolean')
        return value;
    if (typeof value === 'number')
        return Number.isFinite(value) ? value : String(value);
    if (typeof value === 'string') {
        const maxChars = state.limits.maxStringChars;
        if (value.length <= maxChars)
            return value;
        state.complete = false;
        return new ParameterNote('cut', `...[${value.length - maxChars} chars omitted]`, value.slice(0, maxChars));
    }
    if (typeof value === 'undefined')
        return new ParameterNote('omitted', '[undefined]');
    if (typeof value === 'bigint')
        return value.toString();
    if (typeof value !== 'object')
        return new ParameterNote('omitted', `[${typeof value}]`);
    if (depth >= state.maxDepth) {
        state.complete = false;
        return new ParameterNote('omitted', '[nested value omitted]');
    }
    if (seen.has(value)) {
        state.complete = false;
        return new ParameterNote('omitted', '[circular value omitted]');
    }
    seen.add(value);
    try {
        if (Array.isArray(value)) {
            const projected = value.slice(0, state.maxArrayItems)
                .map((item) => safeParameterValue(item, '', depth + 1, seen, state));
            if (value.length > state.maxArrayItems) {
                state.complete = false;
                projected.push(new ParameterNote('more', `[${value.length - state.maxArrayItems} items omitted]`));
            }
            return projected;
        }
        const entries = Object.entries(value)
            .sort(([left], [right]) => left.localeCompare(right));
        const projected = Object.create(null);
        for (const [entryKey, entryValue] of entries.slice(0, state.maxEntries))
            projected[entryKey] = safeParameterValue(entryValue, entryKey, depth + 1, seen, state);
        if (entries.length > state.maxEntries) {
            state.complete = false;
            projected['[truncated]'] = new ParameterNote('more', `${entries.length - state.maxEntries} fields omitted`);
        }
        return projected;
    }
    finally {
        seen.delete(value);
    }
}
function projectActionParameters(input, limits) {
    try {
        // A wide card lists every field and item: each takes at least two characters, so the total limit binds first.
        const wide = isWideConfirm(limits);
        const listCap = Math.ceil(limits.maxTotalChars / 2);
        const state = {
            complete: true,
            limits,
            maxDepth: wide ? WIDE_PARAMETER_MAX_DEPTH : PARAMETER_MAX_DEPTH,
            maxEntries: wide ? listCap : PARAMETER_MAX_ENTRIES,
            maxArrayItems: wide ? listCap : PARAMETER_MAX_ARRAY_ITEMS,
        };
        const projected = safeParameterValue(input, '', 0, new WeakSet(), state);
        const serialized = JSON.stringify(projected, null, 2);
        if (!serialized)
            return { text: '{}', complete: state.complete };
        // A wide (Office) card's total limit counts the parameters (compact JSON), not the indentation the card shows them
        // with (contract §5): a legal 2,000-cell range of short text stays confirmable. The display stays indented unless
        // that is many times the limit (deep nesting); the compact form is then shown, still whole.
        const measured = wide ? JSON.stringify(projected) : serialized;
        if (measured.length <= limits.maxTotalChars)
            return { text: wide && serialized.length > limits.maxTotalChars * WIDE_INDENTED_DISPLAY_FACTOR ? measured : serialized, complete: state.complete, value: projected };
        return {
            text: `${serialized.slice(0, limits.maxTotalChars)}\n...[parameter display truncated]`,
            complete: false,
        };
    }
    catch {
        return { text: '[parameters could not be displayed]', complete: false };
    }
}
/**
 * A bounded, deterministic, credential-redacted projection of the exact server-bound action input. `limits` defaults to
 * the desktop and web card (512 characters per string, 4,096 in all of the display); Office cards pass wider ones
 * (confirmLimitsFor), whose total counts the parameters themselves rather than the display's indentation.
 */
export function formatActionParameters(input, limits = DEFAULT_CONFIRM_LIMITS) {
    return projectActionParameters(input, limits).text;
}
export function actionParametersAreFullyDisplayable(input, limits = DEFAULT_CONFIRM_LIMITS) {
    return projectActionParameters(input, limits).complete;
}
/** Everything a confirm card shows of an action's input, from one projection. */
export function describeActionParameters(input, limits = DEFAULT_CONFIRM_LIMITS, options = {}) {
    const projected = projectActionParameters(input, limits);
    let readable = null;
    if (projected.value !== undefined) {
        try {
            readable = readableParameters(projected.value, options);
        }
        catch {
            readable = null;
        }
    }
    return { text: projected.text, complete: projected.complete, readable };
}
/** The readable view of an action's input, or null when the card shows the exact view alone (see describeActionParameters). */
export function readableActionParameters(input, limits = DEFAULT_CONFIRM_LIMITS, options = {}) {
    return describeActionParameters(input, limits, options).readable;
}
/** Nested sub-rows beyond this depth show as their exact JSON (the projection's own depth limit still applies first). */
const READABLE_MAX_DEPTH = 6;
const ZONE_KEYS = new Set(['timeZone', 'timezone', 'time_zone', 'tz']);
/** A start and an end that are shown together as "When" when they fall on the same day. */
const TIME_PAIRS = [
    ['start', 'end'], ['startDateTime', 'endDateTime'], ['startTime', 'endTime'], ['start_time', 'end_time'], ['startDate', 'endDate'],
];
const KEY_LABELS = {
    subject: 'Subject', body: 'Body', start: 'Start', end: 'End', startDateTime: 'Start', endDateTime: 'End',
    timeZone: 'Time zone', timezone: 'Time zone', time_zone: 'Time zone', tz: 'Time zone',
    to: 'To', cc: 'Cc', bcc: 'Bcc', attendees: 'Attendees', location: 'Location', online: 'Online meeting',
    dueDateTime: 'Due', dueDate: 'Due', due: 'Due', replyAll: 'Reply all', isAllDay: 'All day', allDay: 'All day',
};
const WORDS = {
    id: 'ID', ids: 'IDs', url: 'URL', urls: 'URLs', uri: 'URI', html: 'HTML', pdf: 'PDF', sha256: 'SHA-256', utc: 'UTC',
    api: 'API', json: 'JSON', csv: 'CSV', ui: 'UI', cc: 'Cc', bcc: 'Bcc', m365: 'Microsoft 365', mcp: 'MCP',
};
/** Where a field sits on the card: the subject first, then when, where and who, long text last; others keep their order. */
const KEY_ORDER = {
    subject: 0, title: 0, name: 0, displayName: 0,
    start: 1, startDateTime: 1, startTime: 1, start_time: 1, startDate: 1,
    end: 2, endDateTime: 2, endTime: 2, end_time: 2, endDate: 2,
    dueDateTime: 3, dueDate: 3, due: 3, isAllDay: 4, allDay: 4, timeZone: 4, timezone: 4, time_zone: 4, tz: 4,
    location: 5, online: 5, to: 6, cc: 7, bcc: 8, attendees: 9, recipients: 9,
    body: 20, content: 20, text: 20, message: 20, comment: 20, notes: 20, description: 20,
};
const DEFAULT_ORDER = 10;
const WHEN_ORDER = 1;
/**
 * Top-level fields whose value is an opaque id a person cannot check by eye (a Teams chat id such as
 * 19:2da4c29f…@thread.v2), each with its row's label: the readable view names the field and points to "Show exact
 * values", which shows the id whole. Who receives a post and what it says stay readable.
 */
const EXACT_VIEW_ONLY_KEYS = { chatId: 'Chat' };
const EXACT_VIEW_ONLY_NOTE = 'Shown under Show exact values';
const PLAIN_KEY = /^[A-Za-z][A-Za-z0-9]*(?:[_.-][A-Za-z0-9]+)*$/;
// C0 controls other than tab and line breaks, DEL, and the bidirectional overrides and isolates: such text could read
// differently from what it is, so it is shown as its exact (escaped) JSON.
const UNREADABLE_TEXT = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F\u202A-\u202E\u2066-\u2069]/;
/** A field name in words: 'timeZone' is "Time zone", 'message_ids' is "Message IDs". An unusual name is shown as it is. */
export function humanizeParameterKey(key) {
    if (Object.hasOwn(KEY_LABELS, key))
        return KEY_LABELS[key];
    if (key.length > 64 || !PLAIN_KEY.test(key))
        return key;
    const words = key.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2').split(/[\s_.-]+/).filter(Boolean);
    return words.map((word, index) => {
        const lower = word.toLowerCase();
        if (Object.hasOwn(WORDS, lower))
            return WORDS[lower];
        return index === 0 ? lower.charAt(0).toUpperCase() + lower.slice(1) : lower;
    }).join(' ');
}
function isRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value) && !(value instanceof ParameterNote);
}
function exactValue(value) {
    let json;
    try {
        json = JSON.stringify(value, null, 2);
    }
    catch {
        json = undefined;
    }
    return { kind: 'exact', json: json ?? String(value) };
}
/** The readable view of a projected input (projectActionParameters' value); null when it is not a non-empty object. */
export function readableParameters(projected, options = {}) {
    if (!isRecord(projected) || !Object.keys(projected).length)
        return null;
    const { rows, more } = readableFields(projected, 1, options);
    return { rows, ...(more !== undefined ? { more } : {}) };
}
function readableFields(record, depth, options) {
    const keys = Object.keys(record);
    const used = new Set();
    let more;
    const marker = record['[truncated]'];
    if (Object.hasOwn(record, '[truncated]') && marker instanceof ParameterNote && marker.kind === 'more') {
        more = marker.text;
        used.add('[truncated]');
    }
    const zoneKey = keys.find((key) => ZONE_KEYS.has(key) && typeof record[key] === 'string');
    const zone = zoneKey ? usableZone(record[zoneKey]) : undefined;
    const rows = [];
    for (const [startKey, endKey] of TIME_PAIRS) {
        if (!Object.hasOwn(record, startKey) || !Object.hasOwn(record, endKey) || used.has(startKey) || used.has(endKey))
            continue;
        const when = sameDayRange(record[startKey], record[endKey], zone, options);
        if (!when)
            continue;
        rows.push({ keys: [startKey, endKey], label: 'When', value: when, order: WHEN_ORDER });
        used.add(startKey);
        used.add(endKey);
    }
    for (const key of keys) {
        if (used.has(key))
            continue;
        // An opaque id a person cannot check by eye: the row names the field, and "Show exact values" shows the id whole.
        const exactOnly = depth === 1 && Object.hasOwn(EXACT_VIEW_ONLY_KEYS, key) && typeof record[key] === 'string';
        rows.push({
            keys: [key],
            label: exactOnly ? EXACT_VIEW_ONLY_KEYS[key] : humanizeParameterKey(key),
            value: exactOnly
                ? { kind: 'note', text: EXACT_VIEW_ONLY_NOTE, redacted: false }
                : ZONE_KEYS.has(key) && typeof record[key] === 'string' ? zoneValue(record[key], options) : readableValue(record[key], depth, zone, options),
            order: Object.hasOwn(KEY_ORDER, key) ? KEY_ORDER[key] : DEFAULT_ORDER,
        });
    }
    // Two rows must never read the same: where labels collide, each of those rows is labelled with its field names instead.
    const counts = new Map();
    for (const row of rows)
        counts.set(row.label.toLowerCase(), (counts.get(row.label.toLowerCase()) ?? 0) + 1);
    for (const row of rows)
        if ((counts.get(row.label.toLowerCase()) ?? 0) > 1)
            row.label = row.keys.join(' – ');
    const ordered = rows.map((row, index) => ({ row, index })).sort((left, right) => left.row.order - right.row.order || left.index - right.index)
        .map(({ row: { keys: rowKeys, label, value } }) => ({ keys: rowKeys, label, value }));
    return { rows: ordered, ...(more !== undefined ? { more } : {}) };
}
function readableValue(value, depth, zone, options) {
    if (value instanceof ParameterNote) {
        if (value.kind !== 'cut')
            return { kind: 'note', text: value.text, redacted: value.kind === 'redacted' };
        return readableText(value.shown) ? { kind: 'text', text: value.shown, cut: value.text } : exactValue(value);
    }
    if (typeof value === 'boolean')
        return { kind: 'text', text: value ? 'Yes' : 'No' };
    if (typeof value === 'number')
        return { kind: 'text', text: String(value) };
    if (typeof value === 'string') {
        if (!readableText(value))
            return exactValue(value);
        return timeValue(value, zone, options) ?? { kind: 'text', text: value };
    }
    if (Array.isArray(value)) {
        // A table (a list of lists, an Excel range) reads better, and stays unambiguous, as its exact JSON.
        if (!value.length || depth >= READABLE_MAX_DEPTH || value.some((item) => Array.isArray(item)))
            return exactValue(value);
        const last = value[value.length - 1];
        const more = last instanceof ParameterNote && last.kind === 'more' ? last.text : undefined;
        const items = (more !== undefined ? value.slice(0, -1) : value).map((item) => readableValue(item, depth + 1, undefined, options));
        return { kind: 'list', items, ...(more !== undefined ? { more } : {}) };
    }
    if (isRecord(value)) {
        if (!Object.keys(value).length || depth >= READABLE_MAX_DEPTH)
            return exactValue(value);
        const { rows, more } = readableFields(value, depth + 1, options);
        return { kind: 'fields', rows, ...(more !== undefined ? { more } : {}) };
    }
    return exactValue(value);
}
/** Text that reads as itself: not empty or blank, and free of control and direction-changing characters. */
function readableText(text) {
    return text.trim().length > 0 && !UNREADABLE_TEXT.test(text);
}
// ---- Dates and times ------------------------------------------------------------------------------------------------
const ISO_DATE_TIME = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,9}))?)?(Z|[+-]\d{2}:?\d{2})?)?$/;
/** An ISO 8601 date or date-time, or null for anything else (whole-second precision only: nothing is ever rounded). */
function parseIsoTime(value) {
    const match = ISO_DATE_TIME.exec(value);
    if (!match)
        return null;
    const [, y, mo, d, h, mi, s, fraction, offset] = match;
    const year = Number(y), month = Number(mo), day = Number(d);
    const hour = h === undefined ? 0 : Number(h), minute = mi === undefined ? 0 : Number(mi), second = s === undefined ? 0 : Number(s);
    if (year < 1000 || month < 1 || month > 12 || day < 1 || hour > 23 || minute > 59 || second > 59)
        return null;
    if (fraction !== undefined && /[1-9]/.test(fraction))
        return null;
    if (offset !== undefined && h === undefined)
        return null;
    const wall = Date.UTC(year, month - 1, day, hour, minute, second);
    if (new Date(wall).getUTCDate() !== day)
        return null; // 2026-02-30 is not a date
    let offsetMinutes = 0;
    if (offset !== undefined && offset !== 'Z') {
        const digits = offset.replace(':', '');
        const hours = Number(digits.slice(1, 3)), minutes = Number(digits.slice(3, 5));
        if (hours > 23 || minutes > 59)
            return null;
        offsetMinutes = (offset.startsWith('-') ? -1 : 1) * (hours * 60 + minutes);
    }
    return { ms: wall - offsetMinutes * 60_000, hasTime: h !== undefined, seconds: second !== 0, instant: offset !== undefined };
}
function dateTimeFormat(options, timeZone, withTime, seconds) {
    const parts = { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric', timeZone };
    if (withTime) {
        parts.hour = 'numeric';
        parts.minute = '2-digit';
        if (seconds)
            parts.second = '2-digit';
    }
    return new Intl.DateTimeFormat(options.locale, parts);
}
function localZone(options) {
    return options.timeZone ?? new Intl.DateTimeFormat().resolvedOptions().timeZone;
}
/** A zone's everyday name ("Pacific Time"), or undefined when the runtime does not know the zone (a Windows zone name). */
function zoneName(zone, at, options) {
    try {
        return new Intl.DateTimeFormat(options.locale, { timeZone: zone, timeZoneName: 'longGeneric' }).formatToParts(at)
            .find((part) => part.type === 'timeZoneName')?.value;
    }
    catch {
        return undefined;
    }
}
/** A sibling time zone value that may name the zone of a wall time: short, one line, plain. */
function usableZone(value) {
    const zone = value.trim();
    return zone.length > 0 && zone.length <= 64 && readableText(zone) && !/\n/.test(zone) ? zone : undefined;
}
function zoneValue(value, options) {
    if (!readableText(value))
        return exactValue(value);
    const zone = usableZone(value);
    const name = zone ? zoneName(zone, Date.now(), options) : undefined;
    return { kind: 'text', text: name && name !== zone ? `${name} (${value})` : value };
}
/**
 * A date or date-time written out. With an offset it names one instant, shown in this computer's zone with that zone's
 * name; without one (a calendar event's start and end are wall times in the sibling time zone) the wall time is shown as
 * given, with the sibling zone's name, and never converted.
 */
function timeValue(value, zone, options) {
    const parsed = parseIsoTime(value);
    if (!parsed)
        return null;
    try {
        if (!parsed.hasTime)
            return { kind: 'time', text: dateTimeFormat(options, 'UTC', false, false).format(parsed.ms), exact: value };
        if (parsed.instant) {
            const here = localZone(options);
            const text = dateTimeFormat(options, here, true, parsed.seconds).format(parsed.ms);
            return { kind: 'time', text: `${text} (${zoneName(here, parsed.ms, options) ?? here})`, exact: value };
        }
        const text = dateTimeFormat(options, 'UTC', true, parsed.seconds).format(parsed.ms);
        return { kind: 'time', text: zone ? `${text} (${zoneName(zone, parsed.ms, options) ?? zone})` : text, exact: value };
    }
    catch {
        return null;
    }
}
/** "Mon, Oct 12, 2026, 8:00 – 8:30 PM (Pacific Time)" for a start and an end on the same day; null otherwise. */
function sameDayRange(start, end, zone, options) {
    if (typeof start !== 'string' || typeof end !== 'string')
        return null;
    const from = parseIsoTime(start), to = parseIsoTime(end);
    if (!from || !to || !from.hasTime || !to.hasTime || from.instant !== to.instant || from.ms > to.ms)
        return null;
    try {
        const displayZone = from.instant ? localZone(options) : 'UTC';
        const day = new Intl.DateTimeFormat('en-US', { year: 'numeric', month: '2-digit', day: '2-digit', timeZone: displayZone });
        if (day.format(from.ms) !== day.format(to.ms))
            return null;
        const format = dateTimeFormat(options, displayZone, true, from.seconds || to.seconds);
        if (typeof format.formatRange !== 'function')
            return null;
        const range = format.formatRange(from.ms, to.ms);
        const suffix = from.instant ? zoneName(displayZone, from.ms, options) ?? displayZone : zone ? zoneName(zone, from.ms, options) ?? zone : undefined;
        return { kind: 'time', text: suffix ? `${range} (${suffix})` : range, exact: `${start} – ${end}` };
    }
    catch {
        return null;
    }
}
// ---- The tool line --------------------------------------------------------------------------------------------------
const TOOL_NAME = /^[a-zA-Z0-9_.:-]{1,160}$/;
/**
 * The card's quiet tool line: "Microsoft 365 · Create event" when the connector is known (the widget's own connector
 * names, or the card's system), from the tool's own name in words; otherwise null, and the card shows the tool id.
 */
export function actionToolLabel(tool, app, appLabel) {
    if (!TOOL_NAME.test(tool))
        return null;
    const connector = toolConnectorLabel(tool) ?? (app && appLabel ? appLabel : undefined);
    if (!connector)
        return null;
    const name = tool.split(/__|[.:]/).filter(Boolean).at(-1) ?? '';
    const words = name.split('_').filter(Boolean);
    const first = words[0]?.toLowerCase() ?? '';
    // Drop the connector's own prefix ('m365_', 'outlook_', 'uplink_'): the line names the connector already.
    if (words.length > 1 && (toolConnectorLabel(first) === connector || (app !== undefined && app.toLowerCase().split('-')[0] === first)))
        words.shift();
    const action = words.join('_');
    if (!PLAIN_KEY.test(action))
        return null;
    return `${connector} · ${humanizeParameterKey(action)}`;
}
//# sourceMappingURL=actionParameters.js.map